import { omit } from "lodash";
import type React from "react";
import type { CategoryFilterOptions } from "services";

import { getStaticFilterOptions } from "~/core";
import { useLocale, useRootLoaderData } from "~/remix-lib";
import {
  FilterChip,
  FilterChipItem,
  FilterChipList,
} from "~/services/FilterChipList";
import getFleamarketArticlePrice from "~/utils/getFleamarketArticlePrice";

import { useLocation } from "@remix-run/react";
import type { BuySellAllPageFilters } from "./BuySellAllPageFilters";
import { BuySellAllPageFilterKey } from "./BuySellAllPageFilters";
import { BuySellSearchParams } from "./buySellSearchParams";

const onlyOnSaleOptionTextMap: Record<string, string> = {
  true: "판매중",
  false: "모든 매물",
};

type FilterChipType = {
  filterLabel: string;
  key: BuySellAllPageFilterKey;
};

type FilterChipsProps = {
  currentFilters: BuySellAllPageFilters;
  categoryFilterOptions: CategoryFilterOptions;
};
/**
 * @deprecated
 */
const FilterChips: React.FC<FilterChipsProps> = ({
  currentFilters,
  categoryFilterOptions,
}) => {
  const rootData = useRootLoaderData();
  const location = useLocation();
  const { t } = useLocale();

  const staticFilterOptions = getStaticFilterOptions({
    karrotLocalCountryCode: rootData.karrotLocalCountryCode,
  });

  const convertPriceFormat = (price: string) => {
    const wellknownOption = staticFilterOptions.priceOptions.find(
      (opt) => opt.urlFriendlyValue === price,
    );
    if (wellknownOption) {
      return wellknownOption.label;
    }

    const [priceFrom, priceTo] = price.split("__");

    const localedPrice = {
      from: getFleamarketArticlePrice(
        rootData.karrotLocalCountryCode,
        priceFrom,
      ),
      to: getFleamarketArticlePrice(rootData.karrotLocalCountryCode, priceTo),
    };

    if (!priceFrom && priceTo) {
      return `- ${localedPrice.to}`;
    }
    if (priceFrom && !priceTo) {
      return `${localedPrice.from} -`;
    }
    return `${localedPrice.from} - ${localedPrice.to}`;
  };

  /**
   * 지역을 제외하고 보여줄 필터 옵션들 (카테고리, 가격, 정렬)
   */
  const filters = (
    Object.entries(currentFilters) as Array<
      [keyof FilterChipsProps["currentFilters"], string]
    >
  ).reduce((acc, [key, v]) => {
    switch (key) {
      case "categoryId":
        acc.push({
          key: BuySellAllPageFilterKey.CategoryId,
          filterLabel:
            categoryFilterOptions.categories.find((c) => c.id.toString() === v)
              ?.name || "",
        });
        return acc;

      case "price":
        acc.push({
          key: BuySellAllPageFilterKey.Price,
          filterLabel: convertPriceFormat(v),
        });
        return acc;

      case "onlyOnSale":
        if (onlyOnSaleOptionTextMap[v]) {
          acc.push({
            key: BuySellAllPageFilterKey.OnlyOnSale,
            filterLabel: t("AsideFilter.only_on_sale"),
          });
        }
        return acc;

      default:
        return acc;
    }
  }, [] as FilterChipType[]);

  return (
    <FilterChipList>
      {filters.map((opt) => (
        <FilterChipItem key={opt.filterLabel}>
          <FilterChip
            href={`${location.pathname}${BuySellSearchParams.stringify(
              omit(currentFilters, opt.key),
              rootData.region,
            )}`}
            label={opt.filterLabel}
          />
        </FilterChipItem>
      ))}
    </FilterChipList>
  );
};

export default FilterChips;
